import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useRecoilValue, useSetRecoilState } from 'recoil'

import {
  Flex,
  FormElement,
  InputNumber,
  RadioButtonsGroup,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  Text,
  TEXT_VARIANT_STANDARD,
  TooltipUtils
} from 'mailup-web-components'

import useSelectHelpers from 'Hooks/useSelectHelpers'

import {
  workflowCommonDataState,
  workflowDetails
} from 'Pages/Workflow/recoil/state/data'
import { step2State } from 'Pages/Workflow/recoil/state/step2'

const StartTypeFilters = () => {
  const intl = useIntl()

  const {
    startingFilters,
    startingFiltersCategories
    //
  } = useRecoilValue(workflowCommonDataState)

  const [isDirty, setIsDirty] = useState(false)

  const { Id } = useRecoilValue(workflowDetails)

  const { IdsFilter, LastSendingPeriod, RecipientLimit } =
    useRecoilValue(step2State)
  const setWorkflowDetailsState = useSetRecoilState(step2State)

  //  console.log('### usePreset useState Id', Id, 'IdsFilter', IdsFilter)

  const [usePreset, setUsePreset] = useState(
    Id > 0 && IdsFilter.length > 0 ? 'use-custom' : 'use-preset'
  )

  useEffect(() => {
    if (usePreset === 'use-custom') {
      setWorkflowDetailsState((prev) => ({
        ...prev,
        LastSendingPeriod: undefined
      }))
    } else {
      setWorkflowDetailsState((prev) => ({
        ...prev,
        IdsFilter: []
      }))
    }

    setTimeout(TooltipUtils.rebuild, 100)
  }, [usePreset, setWorkflowDetailsState])

  const requiredFeedback = intl.formatMessage({
    id: 'Load.MailupCommon.Required'
  })

  const wrongFormatFeedback = intl.formatMessage({
    id: 'Load.Common.ValidationWrongFormat'
  })

  const selectFilterProps = useSelectHelpers(
    IdsFilter,
    startingFilters,
    true,
    isDirty
  )

  const handleFiltersChange = (v) => {
    setWorkflowDetailsState((prev) => ({
      ...prev,
      IdsFilter: v,
      LastSendingPeriod: undefined
    }))
    setIsDirty(true)
  }

  const handleLastSendingPeriodChange = (v) => {
    setWorkflowDetailsState((prev) => ({
      ...prev,
      LastSendingPeriod: v,
      IdsFilter: []
    }))
    setIsDirty(true)
  }

  const handleRecipientLimitChange = (v) => {
    setWorkflowDetailsState((prev) => ({ ...prev, RecipientLimit: v }))
    setIsDirty(true)
  }

  const idsFilterHasFeedback = isDirty && IdsFilter.length === 0

  const lspHasFeedback =
    isDirty && (LastSendingPeriod === undefined || LastSendingPeriod <= 0)

  const recipientLimitHasFeedback =
    isDirty && (RecipientLimit === null || RecipientLimit === undefined)

  const usePresetOptions = [
    {
      label: intl.formatMessage({
        id: 'Load.AutomationWorkflow.UsePresetFilters'
      }),
      value: 'use-preset',
      'data-mu-test': 'use-preset'
    },
    {
      label: intl.formatMessage({
        id: 'Load.AutomationWorkflow.UseCustomFilters'
      }),
      value: 'use-custom',
      'data-mu-test': 'use-custom'
    }
  ]

  const lastSendingPeriodOptions = useMemo(() => {
    return Array.from({ length: 6 })
      .map((_, idx) => idx + 1)
      .reverse()
      .map((o) => ({
        label: intl.formatMessage(
          {
            id: 'Load.AutomationWorkflow.LastSendingPeriodOption'
          },
          {
            period: o
          }
        ),
        value: o
      }))
  }, [intl])

  return (
    <Flex flexDirection='column'>
      <Flex width='100%' justifyContent='center' margin='0 0 24px 0'>
        <RadioButtonsGroup
          items={usePresetOptions}
          onChange={setUsePreset}
          value={usePreset}
        />
      </Flex>

      <Flex flexDirection='column' gap='24px'>
        {usePreset === 'use-custom' && (
          <FormElement
            label={intl.formatMessage({
              id: 'Load.AutomationWorkflow.SelectFilters'
            })}
            required
            feedback={idsFilterHasFeedback ? requiredFeedback : undefined}
            variant={idsFilterHasFeedback ? 'danger' : undefined}
          >
            <Select
              data-mu-test='ids-filter'
              searchable
              placeholder={intl.formatMessage({
                id: 'Load.AutomationWorkflow.SelectExistingFilterPlaceholder'
              })}
              multiple
              variant={SELECT_VARIANT_WITH_CHECKBOX}
              value={IdsFilter}
              options={startingFilters}
              optionGroups={startingFiltersCategories}
              onChange={handleFiltersChange}
              {...selectFilterProps}
            />
          </FormElement>
        )}

        {usePreset === 'use-preset' && (
          <Flex flexDirection='column' gap='16px'>
            <Text variant={TEXT_VARIANT_STANDARD}>
              {intl.formatMessage(
                {
                  id: 'Load.AutomationWorkflow.UsePresetFiltersDescription'
                },
                { br: <br />, strong: (chunks) => <strong>{chunks}</strong> }
              )}
            </Text>
            <FormElement
              label={intl.formatMessage({
                id: 'Load.AutomationWorkflow.SelectLastSendingPeriod'
              })}
              labelHelp={intl.formatMessage({
                id: 'Load.AutomationWorkflow.SelectLastSendingPeriodHelp'
              })}
              required
              feedback={lspHasFeedback ? requiredFeedback : undefined}
              variant={lspHasFeedback ? 'danger' : undefined}
            >
              <Select
                data-mu-test='last-sending-period'
                searchable
                placeholder={intl.formatMessage({
                  id: 'Load.AutomationWorkflow.SelectLastSendingPeriodPlaceholder'
                })}
                value={LastSendingPeriod}
                options={lastSendingPeriodOptions}
                onChange={handleLastSendingPeriodChange}
              />
            </FormElement>
          </Flex>
        )}

        <FormElement
          label={intl.formatMessage({
            id: 'Load.AutomationWorkflow.RecipientLimit'
          })}
          labelHelp={intl.formatMessage({
            id: 'Load.AutomationWorkflow.RecipientLimitHelp'
          })}
          hint={intl.formatMessage({
            id: 'Load.AutomationWorkflow.RecipientLimitHint'
          })}
          feedback={recipientLimitHasFeedback ? wrongFormatFeedback : undefined}
          variant={recipientLimitHasFeedback ? 'danger' : undefined}
        >
          <InputNumber
            min={0}
            max={1000000}
            value={RecipientLimit}
            data-mu-test='recipient-limit'
            onChange={handleRecipientLimitChange}
            variant={recipientLimitHasFeedback ? 'danger' : undefined}
          />
        </FormElement>
      </Flex>
    </Flex>
  )
}

export default StartTypeFilters
